import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Pencil, Trash2, Clock, Tag } from "lucide-react";

const CATEGORY_LABELS = {
    food_service: "Food Service",
    refrigeration: "Refrigeration",
    shelving: "Shelving & Displays",
    counter: "Counter",
    store: "Store Areas",
    exterior: "Exterior",
    other: "Other / Unclassified",
};
const CATEGORY_ORDER = ["food_service", "refrigeration", "shelving", "counter", "store", "exterior", "other"];

const PIPELINE_LABELS = {
    standard: "Standard (Claude vision)",
    ai_autodetect: "AI Autodetect (new)",
};

const CADENCE_LABELS = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
const ALL_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function scheduleSummary(s) {
    if (s.cadence === "daily") {
        const times = (s.scanTimes || "").split(",").filter(Boolean).join(", ");
        const days = s.daysOfWeek ? ` (${s.daysOfWeek.split(",").join(", ")})` : "";
        let weekend = "";
        if (s.weekendScanTimes !== null && s.weekendScanTimes !== undefined) {
            const wt = String(s.weekendScanTimes).trim();
            weekend = wt === "" ? "; no weekend scans" : `; weekends at ${wt.split(",").join(", ")}`;
        }
        return `Daily at ${times || "?"}${days}${weekend}`;
    }
    if (s.cadence === "weekly") {
        const days = (s.daysOfWeek || "Mon").split(",").join(", ");
        const time = (s.scanTimes || "07:00").split(",")[0];
        return `Weekly on ${days} at ${time}`;
    }
    if (s.cadence === "monthly") {
        const time = (s.scanTimes || "07:00").split(",")[0];
        return `Monthly on day ${s.dayOfMonth || 1} at ${time}`;
    }
    return s.cadence;
}

const EMPTY_FORM = {
    id: null, fixtureType: "", fixtureSubtype: "", checkType: "stock",
    cadence: "daily", times: ["07:00", "11:00", "15:00"], days: [],
    weekendMode: "same", weekendTimes: ["07:00"],  // same=follow weekday, off=no weekend scans, custom=own times
    dayOfMonth: 1, pipeline: "standard", priority: 50, notes: "", isActive: true,
};

// Map a stored weekend_scan_times value -> form weekend mode + times.
//   null/undefined -> weekends follow weekday times ("same")
//   '' (empty)     -> no weekend scans ("off")
//   'HH:MM,...'     -> custom weekend times
function weekendFromStored(weekendScanTimes) {
    if (weekendScanTimes === null || weekendScanTimes === undefined) return { weekendMode: "same", weekendTimes: ["07:00"] };
    if (String(weekendScanTimes).trim() === "") return { weekendMode: "off", weekendTimes: ["07:00"] };
    return { weekendMode: "custom", weekendTimes: String(weekendScanTimes).split(",").filter(Boolean) };
}

// Map form weekend mode + times -> stored weekend_scan_times value (daily only).
function weekendToStored(form) {
    if (form.cadence !== "daily") return null;
    if (form.weekendMode === "same") return null;
    if (form.weekendMode === "off") return "";
    return form.weekendTimes.join(",");
}

function FixtureSchedules() {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("pages.schedules");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [actionMsg, setActionMsg] = useState(null);
    const [form, setForm] = useState(null);           // schedule add/edit modal state
    const [saving, setSaving] = useState(false);
    const [subtypeModal, setSubtypeModal] = useState(null); // { fixtureType, displayName }

    const showMsg = (msg, type = "ok") => {
        setActionMsg({ text: msg, type });
        setTimeout(() => setActionMsg(null), 3500);
    };

    const load = useCallback(async () => {
        try {
            const d = await api.fixtureSchedules.load();
            setData(d);
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div style={{ padding: 24, color: "#888" }}>Loading fixture schedules...</div>;
    if (!data) return <div style={{ padding: 24, color: "#888" }}>Failed to load fixture schedules.</div>;

    const subtypesFor = (code) => data.subtypes.filter(s => s.fixtureType === code);
    const schedulesFor = (code) => data.schedules.filter(s => s.fixtureType === code);
    const presetInfo = (code) => {
        const rows = data.presetCounts.filter(p => p.fixtureType === code);
        return {
            presets: rows.reduce((a, r) => a + r.presetCount, 0),
            stores: Math.max(0, ...rows.map(r => r.storeCount), 0),
        };
    };
    const overridesFor = (code) => {
        const r = data.overrideCounts.find(o => o.fixtureType === code);
        return r ? r.overrideCount : 0;
    };

    const visibleTypes = data.types.filter(t => {
        if (!showInactive && !t.isActive) return false;
        if (!search) return true;
        const s = search.toLowerCase();
        return t.displayName.toLowerCase().includes(s) || t.code.toLowerCase().includes(s);
    });

    const byCategory = CATEGORY_ORDER
        .map(cat => ({ cat, types: visibleTypes.filter(t => (t.category || "other") === cat) }))
        .filter(g => g.types.length > 0);

    // ---- schedule modal helpers ----
    const openAddSchedule = (ft) => setForm({ ...EMPTY_FORM, fixtureType: ft, times: ["07:00"] });
    const openEditSchedule = (s) => setForm({
        id: s.id, fixtureType: s.fixtureType, fixtureSubtype: s.fixtureSubtype || "",
        checkType: s.checkType, cadence: s.cadence || "daily",
        times: (s.scanTimes || "07:00").split(",").filter(Boolean),
        days: (s.daysOfWeek || "").split(",").filter(Boolean),
        ...weekendFromStored(s.weekendScanTimes),
        dayOfMonth: s.dayOfMonth || 1, pipeline: s.pipeline || "standard",
        priority: s.priority, notes: s.notes || "", isActive: !!s.isActive,
    });

    const saveSchedule = async () => {
        setSaving(true);
        try {
            const payload = {
                fixtureType: form.fixtureType,
                fixtureSubtype: form.fixtureSubtype || null,
                checkType: form.checkType,
                cadence: form.cadence,
                scanTimes: form.cadence === "monthly"
                    ? form.times.slice(0, 1).join(",")
                    : form.times.join(","),
                weekendScanTimes: weekendToStored(form),
                daysOfWeek: form.cadence === "monthly" ? null : (form.days.length ? form.days.join(",") : null),
                dayOfMonth: form.cadence === "monthly" ? form.dayOfMonth : null,
                pipeline: form.pipeline,
                priority: form.priority,
                notes: form.notes,
                isActive: form.isActive,
            };
            if (form.cadence === "weekly" && !payload.daysOfWeek) {
                showMsg("Weekly schedules need at least one day selected", "err");
                setSaving(false);
                return;
            }
            if (form.id) {
                await api.fixtureSchedules.updateSchedule(form.id, payload);
                showMsg("Schedule updated");
            } else {
                await api.fixtureSchedules.createSchedule(payload);
                showMsg("Schedule added");
            }
            setForm(null);
            load();
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setSaving(false);
        }
    };

    const deleteSchedule = async (s) => {
        if (!window.confirm(`Delete the ${s.checkType} schedule for ${s.fixtureType}${s.fixtureSubtype ? "/" + s.fixtureSubtype : ""}?`)) return;
        try {
            await api.fixtureSchedules.deleteSchedule(s.id);
            showMsg("Schedule deleted");
            load();
        } catch (err) {
            showMsg(err.message, "err");
        }
    };

    const toggleScheduleActive = async (s) => {
        try {
            await api.fixtureSchedules.updateSchedule(s.id, {
                checkType: s.checkType, cadence: s.cadence, scanTimes: s.scanTimes,
                weekendScanTimes: s.weekendScanTimes,
                daysOfWeek: s.daysOfWeek, dayOfMonth: s.dayOfMonth, pipeline: s.pipeline,
                priority: s.priority, notes: s.notes, isActive: !s.isActive,
            });
            showMsg(s.isActive ? "Schedule paused" : "Schedule enabled");
            load();
        } catch (err) {
            showMsg(err.message, "err");
        }
    };

    const saveSubtype = async () => {
        setSaving(true);
        try {
            await api.fixtureSchedules.createSubtype({
                fixtureType: subtypeModal.fixtureType,
                displayName: subtypeModal.displayName,
            });
            showMsg("Subtype added");
            setSubtypeModal(null);
            load();
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setSaving(false);
        }
    };

    const deleteSubtype = async (st) => {
        if (!window.confirm(`Delete subtype "${st.displayName}"?`)) return;
        try {
            await api.fixtureSchedules.deleteSubtype(st.id);
            showMsg("Subtype deleted");
            load();
        } catch (err) {
            showMsg(err.message, "err");
        }
    };

    // ---- time chips editor ----
    const setTime = (idx, val) => setForm(f => {
        const times = [...f.times];
        times[idx] = val;
        return { ...f, times };
    });
    const addTime = () => setForm(f => ({ ...f, times: [...f.times, "12:00"] }));
    const removeTime = (idx) => setForm(f => ({ ...f, times: f.times.filter((_, i) => i !== idx) }));
    // ---- weekend time chips (custom weekend mode) ----
    const setWeekendTime = (idx, val) => setForm(f => {
        const weekendTimes = [...f.weekendTimes];
        weekendTimes[idx] = val;
        return { ...f, weekendTimes };
    });
    const addWeekendTime = () => setForm(f => ({ ...f, weekendTimes: [...f.weekendTimes, "07:00"] }));
    const removeWeekendTime = (idx) => setForm(f => ({ ...f, weekendTimes: f.weekendTimes.filter((_, i) => i !== idx) }));
    const toggleDay = (d) => setForm(f => ({
        ...f,
        days: f.days.includes(d) ? f.days.filter(x => x !== d) : ALL_DAYS.filter(x => [...f.days, d].includes(x)),
    }));

    return (
        <div style={{ padding: "16px 24px", maxWidth: 1200, margin: "0 auto" }}>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: "0 0 4px" }}>Fixture Scan Schedules</h2>
                <p style={{ color: "#888", margin: 0, fontSize: 14 }}>
                    Controls when each fixture type is scanned and which AI pipeline processes it.
                    Times are store-local. Weekly/monthly rows only run on the new dispatcher.
                </p>
            </div>

            {actionMsg && (
                <div style={{
                    padding: "8px 14px", borderRadius: 6, marginBottom: 12, fontSize: 14,
                    background: actionMsg.type === "err" ? "#fef2f2" : "#f0fdf4",
                    color: actionMsg.type === "err" ? "#dc2626" : "#16a34a",
                    border: `1px solid ${actionMsg.type === "err" ? "#fecaca" : "#bbf7d0"}`,
                }}>
                    {actionMsg.text}
                </div>
            )}

            <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
                <input
                    type="text"
                    placeholder="Search fixture types..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                        padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6,
                        fontSize: 14, width: 260,
                    }}
                />
                <label style={{ fontSize: 13, color: "#666", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
                    Show inactive types
                </label>
                <span style={{ fontSize: 13, color: "#888", marginLeft: "auto" }}>
                    {visibleTypes.length} fixture types, {data.schedules.length} schedules
                </span>
            </div>

            {byCategory.map(group => (
                <div key={group.cat} style={{ marginBottom: 24 }}>
                    <h3 style={{
                        fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5,
                        color: "#64748b", margin: "0 0 8px", borderBottom: "1px solid #e2e8f0", paddingBottom: 6,
                    }}>
                        {CATEGORY_LABELS[group.cat] || group.cat}
                    </h3>

                    {group.types.map(t => {
                        const scheds = schedulesFor(t.code);
                        const subs = subtypesFor(t.code);
                        const pi = presetInfo(t.code);
                        const overrides = overridesFor(t.code);
                        return (
                            <div key={t.code} style={{
                                border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 10,
                                opacity: t.isActive ? 1 : 0.55, background: "#fff",
                            }}>
                                {/* Type header */}
                                <div style={{
                                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                                    borderBottom: scheds.length ? "1px solid #f1f5f9" : "none", flexWrap: "wrap",
                                }}>
                                    <div style={{ fontWeight: 600, fontSize: 15 }}>{t.displayName}</div>
                                    <code style={{ fontSize: 12, color: "#999", background: "#f8fafc", padding: "1px 6px", borderRadius: 4 }}>{t.code}</code>
                                    <span style={{ fontSize: 12, color: "#888" }}>
                                        {pi.presets} presets{pi.presets > 0 ? ` across ${pi.stores} stores` : ""}
                                    </span>
                                    {overrides > 0 && (
                                        <span style={{ fontSize: 12, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", padding: "1px 8px", borderRadius: 10 }}>
                                            {overrides} store override{overrides > 1 ? "s" : ""}
                                        </span>
                                    )}
                                    <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                        {subs.map(st => (
                                            <span key={st.id} style={{
                                                fontSize: 12, background: "#eff6ff", color: "#1d4ed8",
                                                border: "1px solid #bfdbfe", borderRadius: 10, padding: "1px 8px",
                                                display: "inline-flex", alignItems: "center", gap: 4,
                                                opacity: st.isActive ? 1 : 0.5,
                                            }}>
                                                <Tag size={11} />{st.displayName}
                                                {canEdit && (
                                                    <button onClick={() => deleteSubtype(st)} title="Delete subtype"
                                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#1d4ed8", padding: 0, fontSize: 12, lineHeight: 1 }}>
                                                        &times;
                                                    </button>
                                                )}
                                            </span>
                                        ))}
                                        {canEdit && (
                                            <>
                                                <button onClick={() => setSubtypeModal({ fixtureType: t.code, displayName: "" })}
                                                    style={smallBtnStyle} title="Add a subtype (e.g. donuts, sandwiches)">
                                                    + Subtype
                                                </button>
                                                <button onClick={() => openAddSchedule(t.code)} style={{ ...smallBtnStyle, background: "#2563eb", color: "#fff", border: "none" }}>
                                                    + Schedule
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Schedule rows */}
                                {scheds.length > 0 && (
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ background: "#f8fafc" }}>
                                                <th style={thStyle}>Applies to</th>
                                                <th style={thStyle}>Check</th>
                                                <th style={thStyle}>Schedule</th>
                                                <th style={thStyle}>Pipeline</th>
                                                <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Priority</th>
                                                <th style={thStyle}>Notes</th>
                                                {canEdit && <th style={{ ...thStyle, width: 130, textAlign: "center" }}>Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scheds.map(s => (
                                                <tr key={s.id} style={{ borderTop: "1px solid #f1f5f9", opacity: s.isActive ? 1 : 0.5 }}>
                                                    <td style={tdStyle}>
                                                        {s.fixtureSubtype
                                                            ? <span style={{ color: "#1d4ed8", fontWeight: 600 }}>{s.fixtureSubtype}</span>
                                                            : <span style={{ color: "#888" }}>All {t.displayName.toLowerCase()}</span>}
                                                    </td>
                                                    <td style={tdStyle}>{s.checkType}</td>
                                                    <td style={tdStyle}>
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                                            <Clock size={13} color="#64748b" />{scheduleSummary(s)}
                                                        </span>
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <span style={{
                                                            fontSize: 12, padding: "1px 8px", borderRadius: 10,
                                                            background: s.pipeline === "ai_autodetect" ? "#f5f3ff" : "#f8fafc",
                                                            color: s.pipeline === "ai_autodetect" ? "#7c3aed" : "#555",
                                                            border: `1px solid ${s.pipeline === "ai_autodetect" ? "#ddd6fe" : "#e2e8f0"}`,
                                                        }}>
                                                            {PIPELINE_LABELS[s.pipeline] || s.pipeline}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: "center", color: "#888" }}>{s.priority}</td>
                                                    <td style={{ ...tdStyle, color: "#888", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.notes || ""}>
                                                        {s.notes || ""}
                                                    </td>
                                                    {canEdit && (
                                                        <td style={{ ...tdStyle, textAlign: "center", whiteSpace: "nowrap" }}>
                                                            <button onClick={() => toggleScheduleActive(s)} style={{ ...smallBtnStyle, marginRight: 4 }}>
                                                                {s.isActive ? "Pause" : "Enable"}
                                                            </button>
                                                            <button onClick={() => openEditSchedule(s)} style={iconBtnStyle} title="Edit">
                                                                <Pencil size={14} color="#2563eb" />
                                                            </button>
                                                            <button onClick={() => deleteSchedule(s)} style={iconBtnStyle} title="Delete">
                                                                <Trash2 size={14} color="#ef4444" />
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}

            {/* Add/Edit schedule modal */}
            {form && (
                <div style={overlayStyle}>
                    <div style={{ ...modalStyle, maxWidth: 520 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                            <h3 style={{ margin: 0 }}>
                                {form.id ? "Edit Schedule" : "Add Schedule"} — {form.fixtureType}
                            </h3>
                            <button onClick={() => setForm(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>&times;</button>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <label style={labelStyle}>
                                Applies to
                                <select value={form.fixtureSubtype} disabled={!!form.id}
                                    onChange={e => setForm(f => ({ ...f, fixtureSubtype: e.target.value }))} style={inputStyle}>
                                    <option value="">All ({form.fixtureType})</option>
                                    {subtypesFor(form.fixtureType).map(st => (
                                        <option key={st.id} value={st.code}>{st.displayName}</option>
                                    ))}
                                </select>
                            </label>
                            <label style={labelStyle}>
                                Check type
                                <select value={form.checkType} onChange={e => setForm(f => ({ ...f, checkType: e.target.value }))} style={inputStyle}>
                                    {data.options.checkTypes.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                                </select>
                            </label>
                            <label style={labelStyle}>
                                Cadence
                                <select value={form.cadence} onChange={e => setForm(f => ({ ...f, cadence: e.target.value }))} style={inputStyle}>
                                    {data.options.cadences.map(c => <option key={c} value={c}>{CADENCE_LABELS[c] || c}</option>)}
                                </select>
                            </label>
                            <label style={labelStyle}>
                                AI pipeline
                                <select value={form.pipeline} onChange={e => setForm(f => ({ ...f, pipeline: e.target.value }))} style={inputStyle}>
                                    {data.options.pipelines.map(p => <option key={p} value={p}>{PIPELINE_LABELS[p] || p}</option>)}
                                </select>
                            </label>
                        </div>

                        {/* Times */}
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                {form.cadence === "daily" ? "Scan times (store-local)" : "Scan time (store-local)"}
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                {(form.cadence === "daily" ? form.times : form.times.slice(0, 1)).map((tm, idx) => (
                                    <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                                        <input type="time" value={tm} onChange={e => setTime(idx, e.target.value)}
                                            style={{ ...inputStyle, width: 110, marginTop: 0 }} />
                                        {form.cadence === "daily" && form.times.length > 1 && (
                                            <button onClick={() => removeTime(idx)} style={iconBtnStyle} title="Remove time">&times;</button>
                                        )}
                                    </span>
                                ))}
                                {form.cadence === "daily" && form.times.length < 12 && (
                                    <button onClick={addTime} style={smallBtnStyle}><Plus size={12} /> time</button>
                                )}
                            </div>
                        </div>

                        {/* Days of week (daily = optional restriction, weekly = required) */}
                        {form.cadence !== "monthly" && (
                            <div style={{ marginTop: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                    Days {form.cadence === "daily" ? "(leave empty for every day)" : ""}
                                </div>
                                <div style={{ display: "flex", gap: 4 }}>
                                    {ALL_DAYS.map(d => (
                                        <button key={d} onClick={() => toggleDay(d)} style={{
                                            padding: "4px 8px", fontSize: 12, borderRadius: 4, cursor: "pointer",
                                            border: `1px solid ${form.days.includes(d) ? "#2563eb" : "#e2e8f0"}`,
                                            background: form.days.includes(d) ? "#eff6ff" : "#fff",
                                            color: form.days.includes(d) ? "#1d4ed8" : "#888",
                                            fontWeight: form.days.includes(d) ? 600 : 400,
                                        }}>
                                            {d}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Weekend override (daily only) */}
                        {form.cadence === "daily" && (
                            <div style={{ marginTop: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                    Weekends (Sat / Sun)
                                </div>
                                <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
                                    {[
                                        { key: "same", label: "Same as weekdays" },
                                        { key: "off", label: "No weekend scans" },
                                        { key: "custom", label: "Custom times" },
                                    ].map(opt => (
                                        <button key={opt.key} onClick={() => setForm(f => ({ ...f, weekendMode: opt.key }))} style={{
                                            padding: "4px 10px", fontSize: 12, borderRadius: 4, cursor: "pointer",
                                            border: `1px solid ${form.weekendMode === opt.key ? "#2563eb" : "#e2e8f0"}`,
                                            background: form.weekendMode === opt.key ? "#eff6ff" : "#fff",
                                            color: form.weekendMode === opt.key ? "#1d4ed8" : "#888",
                                            fontWeight: form.weekendMode === opt.key ? 600 : 400,
                                        }}>
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                {form.weekendMode === "custom" && (
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                        {form.weekendTimes.map((tm, idx) => (
                                            <span key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                                                <input type="time" value={tm} onChange={e => setWeekendTime(idx, e.target.value)}
                                                    style={{ ...inputStyle, width: 110, marginTop: 0 }} />
                                                {form.weekendTimes.length > 1 && (
                                                    <button onClick={() => removeWeekendTime(idx)} style={iconBtnStyle} title="Remove time">&times;</button>
                                                )}
                                            </span>
                                        ))}
                                        {form.weekendTimes.length < 12 && (
                                            <button onClick={addWeekendTime} style={smallBtnStyle}><Plus size={12} /> time</button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Day of month */}
                        {form.cadence === "monthly" && (
                            <label style={{ ...labelStyle, marginTop: 12, display: "block" }}>
                                Day of month (1-28)
                                <input type="number" min={1} max={28} value={form.dayOfMonth}
                                    onChange={e => setForm(f => ({ ...f, dayOfMonth: parseInt(e.target.value || "1", 10) }))}
                                    style={{ ...inputStyle, width: 90 }} />
                            </label>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 12, marginTop: 12 }}>
                            <label style={labelStyle}>
                                Priority
                                <input type="number" min={0} max={999} value={form.priority}
                                    onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value || "50", 10) }))}
                                    style={inputStyle} />
                            </label>
                            <label style={labelStyle}>
                                Notes
                                <input type="text" value={form.notes} maxLength={200}
                                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                                    style={inputStyle} placeholder="e.g. Donuts baked 6am - scan after 8" />
                            </label>
                        </div>

                        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, marginTop: 12, cursor: "pointer" }}>
                            <input type="checkbox" checked={form.isActive}
                                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                            Active
                        </label>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
                            <button onClick={() => setForm(null)} style={{ ...smallBtnStyle, padding: "8px 16px" }}>Cancel</button>
                            <button onClick={saveSchedule} disabled={saving} style={{
                                padding: "8px 16px", background: "#2563eb", color: "#fff", border: "none",
                                borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600,
                            }}>
                                {saving ? "Saving..." : (form.id ? "Save changes" : "Add schedule")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add subtype modal */}
            {subtypeModal && (
                <div style={overlayStyle}>
                    <div style={{ ...modalStyle, maxWidth: 380 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                            <h3 style={{ margin: 0 }}>Add Subtype — {subtypeModal.fixtureType}</h3>
                            <button onClick={() => setSubtypeModal(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>&times;</button>
                        </div>
                        <label style={labelStyle}>
                            Subtype name
                            <input type="text" value={subtypeModal.displayName} autoFocus
                                onChange={e => setSubtypeModal(m => ({ ...m, displayName: e.target.value }))}
                                onKeyDown={e => { if (e.key === "Enter" && subtypeModal.displayName.trim()) saveSubtype(); }}
                                style={inputStyle} placeholder="e.g. Donuts" />
                        </label>
                        <p style={{ fontSize: 12, color: "#888", margin: "8px 0 0" }}>
                            Subtypes let one fixture type carry different content (e.g. a Pie Face cabinet
                            holding donuts or sandwiches) with its own schedule. Assign subtypes to presets
                            on the Presets page.
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                            <button onClick={() => setSubtypeModal(null)} style={{ ...smallBtnStyle, padding: "8px 16px" }}>Cancel</button>
                            <button onClick={saveSubtype} disabled={saving || !subtypeModal.displayName.trim()} style={{
                                padding: "8px 16px", background: "#2563eb", color: "#fff", border: "none",
                                borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600,
                            }}>
                                {saving ? "Saving..." : "Add subtype"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const thStyle = {
    padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 12,
    color: "#555", borderBottom: "1px solid #e2e8f0",
};
const tdStyle = { padding: "8px 12px", verticalAlign: "middle" };
const iconBtnStyle = {
    background: "none", border: "none", cursor: "pointer", padding: 4,
    borderRadius: 4, display: "inline-flex", alignItems: "center", verticalAlign: "middle",
};
const smallBtnStyle = {
    padding: "4px 10px", fontSize: 12, borderRadius: 4, border: "1px solid #e2e8f0",
    cursor: "pointer", background: "#fff", color: "#555",
    display: "inline-flex", alignItems: "center", gap: 4,
};
const overlayStyle = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9998, padding: 16,
};
const modalStyle = {
    background: "#fff", borderRadius: 12, padding: 24, width: "100%",
    maxHeight: "85vh", overflowY: "auto",
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: "#444" };
const inputStyle = {
    width: "100%", padding: "7px 9px", border: "1px solid #d1d5db", borderRadius: 6,
    fontSize: 13, boxSizing: "border-box", marginTop: 4, display: "block", fontWeight: 400,
};

export default FixtureSchedules;
